import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {SearchType} from 'types/search';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import DotsMenu from 'components/DotsMenu';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import TrashIcon from 'assets/images/icons/TrashIcon';
import usePermissions from 'hooks/usePermissions';
import {BillingSolution} from 'types/billingSolution';
import {BillingSolutionThunks} from './billingSolutionThunks';
import {setIsEditing, setBillingSolution} from './billingSolutionSlice';
import {setActiveTab, PROGRAM_TABS} from 'features/programs/programsSlice';
import {capitalizeFirstLetter} from '../../../../utils/caseConverter';

const BillingSolutionTable: React.FC<{openDrawer: () => void}> = ({
  openDrawer,
}) => {
  const billingSolutions = useSelector(
    (state: RootState) => state.billingSolution.billingSolutions,
  );
  const dispatch = useDispatch<AppDispatch>();
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const loading = useSelector(
    (state: RootState) => state.program.status === 'loading',
  );
  const pagy = useSelector((state: RootState) => state.billingSolution.pagy);

  interface VisibleColumnsType {
    name: boolean;
    unitConversionType: boolean;
    aggregation: boolean;
    period: boolean;
  }

  const [visibleColumns, setVisibleColumns] =
    React.useState<VisibleColumnsType>({
      name: true,
      unitConversionType: true,
      aggregation: true,
      period: true,
    });

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {
      label: 'Conversion Type',
      value: 'unitConversionType',
      isVisible: visibleColumns.unitConversionType,
    },
    {
      label: 'Aggregation',
      value: 'aggregation',
      isVisible: visibleColumns.aggregation,
    },
    {label: 'Period', value: 'period', isVisible: visibleColumns.period},
  ];

  const searchByOptions = [{label: 'Name', value: 'name', operator: 'like'}];
  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    await dispatch(
      BillingSolutionThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenNewBillingSolution = () => {
    dispatch(
      setBillingSolution({
        name: '',
        unitConversionType: '',
        aggregation: '',
        period: '',
      }),
    );
    dispatch(setIsEditing(true));
    openDrawer();
  };
  const handleDelete = (id: string) => {
    setDotsMenuIsOpen(null);
    dispatch(BillingSolutionThunks.delete(id));
  };
  const handleEdit = (id: string) => {
    dispatch(setIsEditing(true));
    openDrawer();
    dispatch(BillingSolutionThunks.show(id));
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      tabs={PROGRAM_TABS}
      activeTab={'Billing Solutions'}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      recordsName={'Billing Solutions'}
      columnOptions={columnOptions}
      searchable={true}
      loading={loading}
      columns={true}
      blueButton={
        checkPermission('create', 'BillingSolutions') ? (
          <BlueButton
            onClick={handleOpenNewBillingSolution}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
          {visibleColumns.unitConversionType && (
            <TableHeader label="Conversion Type" sortBy="conversionType" />
          )}
          {visibleColumns.aggregation && <TableHeader label="Aggregation" />}
          {visibleColumns.period && <TableHeader label="Period" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {billingSolutions.map((item: BillingSolution) => {
          const userOptions = [];
          if (checkPermission('update', 'CustomData')) {
            userOptions.push({
              label: 'Edit',
              onClick: () => handleEdit(item.id),
              hoverClass: 'hover:bg-cyan-100',
            });
          }
          if (checkPermission('delete', 'CustomData')) {
            userOptions.push({
              label: 'Delete',
              onClick: () => handleDelete(item.id),
              icon: <TrashIcon />,
              hoverClass: 'hover:bg-red-100 hover:text-red-800',
            });
          }
          return (
            <TableRow key={item.id}>
              {visibleColumns.name && (
                <TableCell>
                  <div className="relative group">
                    <div className="w-full flex items-center gap-x-3">
                      <div className="flex-grow">
                        <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                          {item.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </TableCell>
              )}
              {visibleColumns.unitConversionType && (
                <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                  {item.unitConversionType === 'ratio' && 'Ratio'}
                  {item.unitConversionType === 'range' && 'Number Range'}
                  {item.unitConversionType === 'ratio_partial' &&
                    'Ratio Partial Unit'}
                </TableCell>
              )}
              {visibleColumns.aggregation && (
                <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                  {capitalizeFirstLetter(item.totalUnitsBy)}
                </TableCell>
              )}
              {visibleColumns.period && (
                <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                  {capitalizeFirstLetter(item.createClaimsBy)}
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default BillingSolutionTable;
